'use client';

import React, { useEffect, useRef } from 'react';
import { useTheme } from '@/lib/theme/context';

interface StarPoint {
  x: number;
  y: number;
  r: number;
  v: number;
  p: number;
  a: number;
}

export function SpaceBackground() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (resolvedTheme !== 'dark') {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }

    const prefersReducedMotion = typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
    let pts: StarPoint[] = [];
    let isPaused = false;
    let lastTime = 0;

    const createPoints = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;

      const isMobile = w < 768;
      const count = isMobile ? 35 : Math.min(130, Math.round((w * h) / 12000));

      pts = Array.from({ length: count }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: (Math.random() < 0.08 ? 1.8 : Math.random() * 0.9 + 0.4) * dpr,
        v: (Math.random() * 0.2 + 0.05) * dpr,
        p: Math.random() * 6.28,
        a: Math.random() * 0.4 + 0.2,
      }));
    };

    createPoints();

    const handleResize = () => {
      createPoints();
    };

    const handleVisibility = () => {
      if (document.hidden) {
        isPaused = true;
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      } else {
        isPaused = false;
        lastTime = performance.now();
        if (!prefersReducedMotion) {
          rafRef.current = requestAnimationFrame(draw);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibility);

    if (prefersReducedMotion) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of pts) {
        ctx.fillStyle = `rgba(240, 244, 255, ${p.a})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 0.8, 0, 6.283);
        ctx.fill();
      }
      return () => {
        window.removeEventListener('resize', handleResize);
        document.removeEventListener('visibilitychange', handleVisibility);
      };
    }

    const draw = (currentTime: number) => {
      if (isPaused || document.hidden) return;
      rafRef.current = requestAnimationFrame(draw);

      if (currentTime - lastTime < 33) return;
      lastTime = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of pts) {
        const alpha = p.a * (0.6 + 0.4 * Math.sin(currentTime / (800 + p.v * 3000) + p.p));
        if (p.r > 1.3 * dpr) {
          const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3.5);
          glow.addColorStop(0, `rgba(225, 235, 255, ${alpha})`);
          glow.addColorStop(1, 'rgba(150, 165, 255, 0)');
          ctx.fillStyle = glow;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * 3.5, 0, 6.283);
          ctx.fill();
        }
        ctx.fillStyle = `rgba(240, 244, 255, ${Math.min(1, alpha + 0.2)})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 0.7, 0, 6.283);
        ctx.fill();
      }
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibility);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [resolvedTheme]);

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none -z-10 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(50%_30%_at_85%_38%,rgba(120,80,230,0.22),transparent_70%),radial-gradient(45%_25%_at_10%_58%,rgba(60,120,255,0.18),transparent_70%),linear-gradient(180deg,#00030a_0%,#06103a_14%,#0a0f3a_30%,#050a24_52%,#0a1040_74%,#1a1660_92%,#2a1f80_100%)] opacity-100" />
      <div className="absolute -left-[10%] -right-[10%] bottom-0 h-[900px] bg-[radial-gradient(40%_50%_at_18%_88%,rgba(200,90,255,0.26),transparent_70%),radial-gradient(45%_55%_at_85%_85%,rgba(60,130,255,0.30),transparent_70%),radial-gradient(60%_40%_at_50%_100%,rgba(140,170,255,0.25),transparent_70%)] opacity-100 pointer-events-none" />
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none opacity-100"
      />
    </div>
  );
}
